import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import VendorLayout from '../../components/VendorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CreditCard, IndianRupee, TrendingUp, Clock, CheckCircle,
  ArrowUpRight, ArrowDownRight, Calendar, Download, Filter,
  Wallet, Banknote, Percent, AlertCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPrice } from '../../lib/utils';

export default function VendorPayments() {
  const { vendor, token } = useVendor();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [period, setPeriod] = useState('month');
  const navigate = useNavigate();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Authentication is handled by VendorProtectedRoute
    if (vendor && token) {
      fetchEarnings();
    }
  }, [vendor, token, period]);

  const fetchEarnings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/vendor/earnings`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period }
      });
      setEarnings(response.data);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const summary = earnings?.summary || {};
  const payouts = earnings?.payouts || [];
  const commissionRate = earnings?.commission_rate || vendor?.commission?.rate || 10;

  // Calculate values
  const totalRevenue = summary.total_revenue || 0;
  const totalCommission = summary.total_commission || (totalRevenue * commissionRate / 100);
  const netEarnings = summary.net_earnings || (totalRevenue - totalCommission);
  const orderCount = summary.order_count || 0;

  return (
    <VendorLayout>
      <div className="space-y-6" data-testid="vendor-payments-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payments & Earnings</h1>
            <p className="text-gray-500">Track your revenue, commissions, and payouts</p>
          </div>
          <div className="flex gap-2">
            {['week', 'month', 'year', 'all'].map(p => (
              <Button
                key={p}
                variant={period === p ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriod(p)}
                className={period === p ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-green-100">Total Revenue</p>
                    <IndianRupee className="w-5 h-5 text-green-200" />
                  </div>
                  <p className="text-3xl font-bold">{formatPrice(totalRevenue)}</p>
                  <div className="flex items-center gap-1 mt-2 text-sm text-green-100">
                    <ArrowUpRight className="w-4 h-4" />
                    <span>From {orderCount} orders</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-orange-100">Platform Commission</p>
                    <Percent className="w-5 h-5 text-orange-200" />
                  </div>
                  <p className="text-3xl font-bold">{formatPrice(totalCommission)}</p>
                  <div className="flex items-center gap-1 mt-2 text-sm text-orange-100">
                    <span>{commissionRate}% commission rate</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-blue-100">Net Earnings</p>
                    <Wallet className="w-5 h-5 text-blue-200" />
                  </div>
                  <p className="text-3xl font-bold">{formatPrice(netEarnings)}</p>
                  <div className="flex items-center gap-1 mt-2 text-sm text-blue-100">
                    <span>After commission deduction</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-purple-100">Pending Payout</p>
                    <Clock className="w-5 h-5 text-purple-200" />
                  </div>
                  <p className="text-3xl font-bold">{formatPrice(netEarnings)}</p>
                  <div className="flex items-center gap-1 mt-2 text-sm text-purple-100">
                    <span>Next payout in 7 days</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Commission Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="w-5 h-5" />
                  Commission Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Your Commission Rate</p>
                    <p className="text-3xl font-bold text-gray-900">{commissionRate}%</p>
                    <p className="text-xs text-gray-500 mt-1">Platform fee per order</p>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">You Keep</p>
                    <p className="text-3xl font-bold text-green-600">{100 - commissionRate}%</p>
                    <p className="text-xs text-gray-500 mt-1">Of every sale</p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Payout Schedule</p>
                    <p className="text-xl font-bold text-blue-600">Weekly</p>
                    <p className="text-xs text-gray-500 mt-1">Min ₹500 | 7 day hold</p>
                  </div>
                </div>

                {/* Commission Example */}
                <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
                  <p className="text-sm font-medium text-gray-700 mb-3">Example Calculation:</p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Sale Amount:</span>
                      <span className="font-semibold">₹1,000</span>
                    </div>
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Commission ({commissionRate}%):</span>
                      <span className="font-semibold text-red-600">-₹{(1000 * commissionRate / 100).toFixed(0)}</span>
                    </div>
                    <span className="text-gray-400">=</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">You Receive:</span>
                      <span className="font-semibold text-green-600">₹{(1000 * (100 - commissionRate) / 100).toFixed(0)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payout History */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Banknote className="w-5 h-5" />
                  Payout History
                </CardTitle>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                {payouts.length > 0 ? (
                  <div className="space-y-3">
                    {payouts.map((payout, index) => (
                      <div key={payout.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            payout.status === 'completed' ? 'bg-green-100' :
                            payout.status === 'pending' ? 'bg-yellow-100' :
                            'bg-gray-100'
                          }`}>
                            {payout.status === 'completed' ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <Clock className="w-5 h-5 text-yellow-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">Payout #{payout.id?.slice(-6).toUpperCase() || index + 1}</p>
                            <p className="text-sm text-gray-500">
                              {payout.created_at ? new Date(payout.created_at).toLocaleDateString('en-IN') : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{formatPrice(payout.amount)}</p>
                          <Badge className={
                            payout.status === 'completed' ? 'bg-green-100 text-green-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {payout.status?.toUpperCase() || 'PENDING'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Wallet className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No payouts yet</h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                      Payouts are processed weekly for delivered orders. Once you have completed orders, 
                      your earnings will appear here.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bank Account Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Bank Account Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {vendor?.kyc?.bank_account ? (
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Account Number</p>
                      <p className="font-medium">****{vendor.kyc.bank_account.slice(-4)}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">IFSC Code</p>
                      <p className="font-medium">{vendor.kyc.bank_ifsc || 'Not provided'}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Bank Name</p>
                      <p className="font-medium">{vendor.kyc.bank_name || 'Not provided'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 bg-yellow-50 rounded-xl border border-yellow-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-yellow-800">Bank details not provided</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          Please add your bank account details to receive payouts.
                        </p>
                        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/vendor/settings')}>
                          Add Bank Details
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </VendorLayout>
  );
}
